/**
 * KABİLİYET SAĞLAYICISI — kasa açıkken sertifika kapısıyla oturumu yürütür.
 *
 *   - Kasa açılınca (ve eşitlemeyle sertifika gelince) kasadaki sertifikalar
 *     kapıya gösterilir, jeton alınır; uygulama açıkken 15 dakikada bir
 *     yenilenir. Sertifikası olmayan kasa kapıya HİÇ istek atmaz.
 *   - Oturum düştüyse (`OTURUM_YOK`: kasa başka cihazda açıldı) KENDİLİĞİNDEN
 *     yeniden açılmaz (kullanıcı kararı 26.10.2026) — iki cihaz birbirini
 *     sırayla düşürürdü. Kullanıcı "Bu cihazda yeniden aç" der.
 *   - Kilit, otomatik kilit ve profil değişimi oturumu kapatır
 *     (`kapiOturumunuKapat`, `kilitle` çağırır).
 *   - Sunucuya ulaşılamazsa son teyitle 48 saat sürülür.
 *
 * Ekranlar ne açık olduğunu YALNIZCA `useKabiliyet().durum.sinirlar`dan okur.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  KABILIYETLER,
  OTURUM_SURELERI,
  aktivasyonKoduDuzelt,
  sinirlar,
  type EtkinKabiliyetler,
  type OturumResponse,
  type SertifikaGovdesi,
  type Sinirlar,
} from "@parafon/contracts/sertifika";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { fiyatGecmisiAltSiniriAyarla, jetonAyarla, kapiIstegi, kapiTabanAdresi, type KapiIstegi } from "../central/createCentral";
import {
  SON_TEYIT_AYARI,
  cevrimdisiDurum,
  enEskiGun,
  jetonOku,
  kasaKimligiAl,
  kasaKimligiOku,
  oturumSertifikalari,
  sertifikaKaydet,
  sertifikalariOku,
  teyitOku,
  teyitYazilmali,
  teyittenDurum,
  ucretsizDurum,
  type KabiliyetDurumu,
  type Teyit,
} from "./kabiliyet";
import { M } from "../dil";

export interface KabiliyetBaglami {
  readonly durum: KabiliyetDurumu;
  /** Sınır doluyken açılan bu oturum en eskisini düşürdüyse (bir kez söylenir). */
  readonly eskisiDustu: boolean;
  /** Aktivasyon kodunu kullanır; açılan sertifikanın gövdesini döner. */
  kodKullan(kod: string): Promise<SertifikaGovdesi>;
  /** `baska-cihazda` hâlinden sonra: bu cihazda yeni oturum (en eskisini düşürebilir). */
  yenidenAc(): Promise<void>;
}

const VARSAYILAN: KabiliyetBaglami = {
  durum: ucretsizDurum("yukleniyor"),
  eskisiDustu: false,
  kodKullan: async () => {
    throw new Error(M.ayarlar.abonelik.hata.buEkrandaYok);
  },
  yenidenAc: async () => undefined,
};

const Baglam = createContext<KabiliyetBaglami>(VARSAYILAN);

export function useKabiliyet(): KabiliyetBaglami {
  return useContext(Baglam);
}

/** Kurucu sertifikasının sınırları: her şey açık. */
export const SINIRSIZ: Sinirlar = sinirlar(
  Object.fromEntries(KABILIYETLER.map((k) => [k.ad, null])) as EtkinKabiliyetler,
);

/**
 * Ekranların UYGULAYACAĞI sınırlar. Durum henüz bilinmiyorken (yükleniyor),
 * geliştirme kipinde (kapı yok) ve sağlayıcının dışında çizilen ekranlarda
 * sınır UYGULANMAZ: bilinmeyen bir durum kullanıcıyı kısıtlamaz, açılış
 * anında da "kilitli" yanıp sönmez. Bilinen her hâlde `durum.sinirlar`.
 */
export function uygulananSinirlar(d: KabiliyetDurumu): Sinirlar {
  return d.hal === "yukleniyor" || d.hal === "kapi-yok" ? SINIRSIZ : d.sinirlar;
}

export function useSinirlar(): Sinirlar {
  return uygulananSinirlar(useContext(Baglam).durum);
}

/** Testler ve önizlemeler için: sabit bir kabiliyet durumu. */
export const KabiliyetBaglamiSaglayici = Baglam.Provider;

// ---------------------------------------------------------------------------
// Kilitte oturumu kapatma — `kilitle` sağlayıcıyı tanımaz, bu kancayı çağırır.
// ---------------------------------------------------------------------------

let kapatici: (() => Promise<void>) | null = null;

/** Kilitten hemen önce: açık oturum kapıya bildirilir (en çok `bekleMs`). */
export async function kapiOturumunuKapat(bekleMs = 1500): Promise<void> {
  const k = kapatici;
  if (k === null) return;
  await Promise.race([k().catch(() => undefined), new Promise<void>((r) => setTimeout(r, bekleMs))]);
}

// ---------------------------------------------------------------------------

/** Kapının hata kodu (taşıyıcının `CentralHttpError`ı ya da test sahtesi). */
export function kapiHataKodu(err: unknown): string | null {
  const k = (err as { code?: unknown } | null)?.code;
  return typeof k === "string" ? k : null;
}

/** Ağ yok, zaman aşımı, kapı kurulu değil (HTML yanıt) gibi "ulaşılamadı" hâlleri. */
function ulasilamadi(err: unknown): boolean {
  const k = kapiHataKodu(err);
  return k === null || k === "BAD_JSON" || k === "HTTP_ERROR" || k === "INTERNAL" || k === "RATE_LIMITED";
}

export function KabiliyetSaglayici({
  children,
  istek,
  simdi = () => new Date(),
  yenilemeMs = OTURUM_SURELERI.yenilemeSaniye * 1000,
}: {
  readonly children: ReactNode;
  /** Testler sahte verir; `null` = kapı yok. Verilmezse adresten. */
  readonly istek?: KapiIstegi | null;
  readonly simdi?: () => Date;
  readonly yenilemeMs?: number;
}) {
  const { repo, revision } = useRepositoryContext();
  const kapi = useMemo<KapiIstegi | null>(() => {
    if (istek !== undefined) return istek;
    const taban = kapiTabanAdresi();
    return taban === null ? null : kapiIstegi(taban);
  }, [istek]);

  const [durum, setDurum] = useState<KabiliyetDurumu>(ucretsizDurum("yukleniyor"));
  // Jeton: yalnızca sunucu teyitli durumda; başka her hâlde (düştü, süre
  // doldu, ulaşılamıyor, ücretsiz) istekler jetonsuz gider.
  useEffect(() => {
    jetonAyarla(durum.hal === "sunucu" ? jetonRef.current : null);
  }, [durum]);
  useEffect(() => () => jetonAyarla(null), []);

  // Fiyat geçmişi derinliği: merkezden fiyat çeken tek geçide (lazySync) bildirilir.
  useEffect(() => {
    const yil = uygulananSinirlar(durum).fiyatGecmisiYil;
    fiyatGecmisiAltSiniriAyarla(enEskiGun(yil, simdiRef.current().toISOString().slice(0, 10)) ?? null);
  }, [durum]);
  useEffect(() => () => fiyatGecmisiAltSiniriAyarla(null), []);
  const [eskisiDustu, setEskisiDustu] = useState(false);
  const oturum = useRef<string | null>(null);
  // Son alınan jeton; yalnızca durum "sunucu" iken fiyat/kasa isteklerine verilir.
  const jetonRef = useRef<string | null>(null);
  const sertifikaImzasi = useRef<string>("");
  const durdu = useRef(false);
  const simdiRef = useRef(simdi);
  simdiRef.current = simdi;

  const teyitKaydet = useCallback(
    async (t: Teyit) => {
      const eski = teyitOku(await repo.settings.get(SON_TEYIT_AYARI));
      if (teyitYazilmali(eski, t)) await repo.settings.set(SON_TEYIT_AYARI, JSON.stringify(t));
    },
    [repo],
  );

  /** Oturumu açar ya da yeniler; `yeni` = elindeki oturum kimliğini bırak. */
  const tazele = useCallback(
    async (yeni: boolean): Promise<void> => {
      const sertifikalar = await sertifikalariOku(repo);
      const kimlik = await kasaKimligiOku(repo);
      const gidecek = oturumSertifikalari(sertifikalar, kimlik);
      if (gidecek.length === 0) {
        oturum.current = null;
        setDurum(ucretsizDurum("sertifikasiz"));
        return;
      }
      if (kapi === null) {
        setDurum(ucretsizDurum("kapi-yok"));
        return;
      }
      if (yeni) oturum.current = null;
      try {
        const y = (await kapi("/kapi/oturum", {
          sertifikalar: gidecek,
          ...(oturum.current === null ? {} : { oturum: oturum.current }),
        })) as Partial<OturumResponse>;
        const jeton = jetonOku(y.jeton);
        if (jeton === null) throw Object.assign(new Error("kapı jeton göndermedi"), { code: "BAD_JSON" });
        if (y.oturum === "" || Object.keys(jeton.kabiliyetler).length === 0) {
          oturum.current = null;
          setDurum(ucretsizDurum("suresi-doldu", simdiRef.current().toISOString()));
          return;
        }
        oturum.current = typeof y.oturum === "string" ? y.oturum : null;
        if (y.eskisiDustu === true) setEskisiDustu(true);
        const t: Teyit = { an: simdiRef.current().toISOString(), kabiliyetler: jeton.kabiliyetler, bitis: jeton.bitis };
        jetonRef.current = typeof y.jeton === "string" ? y.jeton : null;
        setDurum(teyittenDurum("sunucu", t));
        await teyitKaydet(t);
      } catch (err) {
        const kod = kapiHataKodu(err);
        if (kod === "OTURUM_YOK") {
          oturum.current = null;
          durdu.current = true;
          setDurum(ucretsizDurum("baska-cihazda"));
          return;
        }
        if (kod === "GECERSIZ" || kod === "MUHUR" || kod === "IPTAL") {
          oturum.current = null;
          setDurum(ucretsizDurum("gecersiz"));
          return;
        }
        if (!ulasilamadi(err)) throw err;
        setDurum(cevrimdisiDurum(teyitOku(await repo.settings.get(SON_TEYIT_AYARI)), simdiRef.current()));
      }
    },
    [repo, kapi, teyitKaydet],
  );

  // Kasa açılınca ve eşitlemeyle sertifika kümesi değişince.
  useEffect(() => {
    let iptal = false;
    void (async () => {
      const imza = (await sertifikalariOku(repo)).map((s) => s.govde.no).join(",");
      if (iptal) return;
      if (imza === sertifikaImzasi.current && durum.hal !== "yukleniyor") return;
      sertifikaImzasi.current = imza;
      if (durdu.current) return;
      await tazele(false);
    })().catch(() => {
      if (!iptal) setDurum(ucretsizDurum("teyit-eskidi"));
    });
    return () => {
      iptal = true;
    };
    // `durum.hal` yalnızca ilk açılışı ayırmak için okunuyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, revision, tazele]);

  // Açıkken düzenli yenileme (oturum düştüyse durur).
  useEffect(() => {
    const id = setInterval(() => {
      if (durdu.current || oturum.current === null) return;
      void tazele(false).catch(() => undefined);
    }, yenilemeMs);
    return () => clearInterval(id);
  }, [tazele, yenilemeMs]);

  // Kilitte kapatma kancası.
  useEffect(() => {
    kapatici = async () => {
      const o = oturum.current;
      jetonAyarla(null);
      if (kapi === null || o === null) return;
      oturum.current = null;
      await kapi("/kapi/oturum-bitir", { oturum: o });
    };
    return () => {
      kapatici = null;
    };
  }, [kapi]);

  const kodKullan = useCallback(
    async (ham: string): Promise<SertifikaGovdesi> => {
      if (kapi === null) throw new Error(M.ayarlar.abonelik.hata.kapiYok);
      const kod = aktivasyonKoduDuzelt(ham);
      if (kod === null) throw Object.assign(new Error(M.ayarlar.abonelik.hata.gecersiz), { code: "GECERSIZ" });
      const kimlik = await kasaKimligiAl(repo);
      const y = (await kapi("/kapi/etkinlestir", { kod, kasaKimligi: kimlik })) as { sertifika?: unknown };
      const g = await sertifikaKaydet(repo, y.sertifika, kimlik);
      sertifikaImzasi.current = (await sertifikalariOku(repo)).map((s) => s.govde.no).join(",");
      if (!durdu.current) await tazele(false);
      return g;
    },
    [repo, kapi, tazele],
  );

  const yenidenAc = useCallback(async () => {
    durdu.current = false;
    setEskisiDustu(false);
    await tazele(true);
  }, [tazele]);

  const deger = useMemo<KabiliyetBaglami>(
    () => ({ durum, eskisiDustu, kodKullan, yenidenAc }),
    [durum, eskisiDustu, kodKullan, yenidenAc],
  );
  return <Baglam.Provider value={deger}>{children}</Baglam.Provider>;
}
